// src/layouts/calendar/CalendarMobileLayout.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styles from "../../pages/CalendarGeneral.module.css";
import api from "../../api";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import multiMonthPlugin from "@fullcalendar/multimonth";
import EventCreateModal from "../../components/EventCreateModal";
import EventDetailModal from "../../components/EventDetailModal";
import { EVENT_TYPE_COLORS } from "../../constants/eventTypes";

// 유형·색은 constants/eventTypes.js 단일 출처에서 온다(PC·사이드바와 공유).
const eventColors = EVENT_TYPE_COLORS;

function getHeaderText(view, currentDate) {
  const y = currentDate.getFullYear();
  const m = currentDate.getMonth() + 1;
  const d = currentDate.getDate();
  const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
  const wd = weekdays[currentDate.getDay()];

  if (view === "timeGridDay") return `${y}년 ${m}월 ${d}일 (${wd})`;
  if (view === "timeGridWeek") return `${y}년 ${m}월`;
  if (view === "dayGridMonth") return `${y}년 ${m}월`;
  if (view === "multiMonthYear") return `${y}년`;
  return `${y}년 ${m}월`;
}


function renderEventContent(eventInfo) {
  // ✅ 공휴일: 연한 빨간 배경에 빨간 텍스트로 표시
  if (eventInfo.event.extendedProps.isHoliday) {
    return (
      <div style={{
        color: "#d32f2f",
        backgroundColor: "#ffebee", // 👈 연하고 예쁜 빨간색 배경 추가!
        fontWeight: 700,
        fontSize: "12px",
        padding: "4px 8px",         // 👈 여백을 넓혀서 칸에 자연스럽게 채웁니다.
        borderRadius: "4px",        // 👈 모서리를 살짝 둥글게 처리
        border: "1px solid #ffcdd2", // 👈 테두리도 연한 빨간색으로 깔끔하게 지정
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
        width: "100%",              // 👈 가로 꽉 차게
        boxSizing: "border-box"
      }}>
        {eventInfo.event.title}
      </div>
    );
  }

  const raw = eventInfo.event.extendedProps.raw || {};
  const assignee = raw.assignee_display || "";
  


  const title = raw.title || "";
  const color = eventInfo.event.extendedProps.color || "#e0e0e0";
  const currentView = eventInfo.view.type;

  if (currentView === "dayGridMonth" || currentView === "multiMonthYear") {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: "4px", backgroundColor: color, borderRadius: "4px", padding: "1px 4px", overflow: "hidden" }}>
        <span style={{ fontSize: "11px", color: "#000", fontWeight: "bold", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {assignee || title}
        </span>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: color, borderRadius: "4px", padding: "2px 4px", height: "100%", overflow: "hidden" }}>
      {assignee && (
        <div style={{ fontSize: "12px", fontWeight: "bold", color: "#000", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {assignee}
        </div>
      )}
      {title && (
        <div style={{ fontSize: "11px", color: "#333", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {title}
        </div>
      )}
    </div>
  );
}

export default function CalendarMobileLayout() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState("timeGridDay");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [serverEvents, setServerEvents] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [holidays, setHolidays] = useState([]);   


  const calRef = useRef(null);

  const fetchEvents = useCallback(async () => {
    try {
      const res = await api.get("/calendar/events/");
      setServerEvents(res.data || []);
    } catch (err) {
      console.error("❌ 이벤트 불러오기 실패:", err);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  // ✅ 공휴일 fetch (연도 변경 시)
  useEffect(() => {
    const year = currentDate.getFullYear();
    api.get(`/calendar/holidays/?year=${year}`)
      .then((res) => setHolidays(res.data || []))
      .catch((err) => console.error("공휴일 불러오기 실패", err));
  }, [currentDate.getFullYear()]);

  useEffect(() => {
    const calApi = calRef.current?.getApi?.();
    if (!calApi) return;
    calApi.changeView(view);
  }, [view]);
  


  const fcEvents = useMemo(() => {
    const result = [];
    serverEvents.forEach((ev) => {
      const eventType = ev.event_type || "업무일정";
      const isAllDay = eventType === "공지사항";
      const { start_date, end_date, start_time, end_time } = ev;
      const endDateStr = end_date || start_date;

      if (!isAllDay && start_time && end_time && start_date !== endDateStr) {
        const cur = new Date(start_date);
        const last = new Date(endDateStr);
        while (cur <= last) {
          const d = cur.toISOString().slice(0, 10);
          result.push({
            id: `${ev.id}-${d}`,
            title: ev.title,
            start: `${d}T${start_time}`,
            end: `${d}T${end_time}`,
            allDay: false,
            backgroundColor: "transparent",
            borderColor: "transparent",
            extendedProps: {
              color: eventColors[eventType] || "#90caf9",
              assignee_display: ev.assignee_display,
              position_rank: ev.position_rank ?? 999,
              raw: ev,
            },
          });
          cur.setDate(cur.getDate() + 1);
        }
      } else {
        result.push({
          id: String(ev.id),
          title: ev.title,
          start: start_time ? `${start_date}T${start_time}` : start_date,
          end: end_time ? `${endDateStr}T${end_time}` : endDateStr,
          allDay: isAllDay,
          backgroundColor: "transparent",
          borderColor: "transparent",
          extendedProps: {
            color: eventColors[eventType] || "#90caf9",
            assignee_display: ev.assignee_display,
            position_rank: ev.position_rank ?? 999,
            raw: ev,
          },
        
        
        });
      }
    });
    return result;
  }, [serverEvents]);

  // ✅ 공휴일 Map
  const holidayMap = useMemo(() => {
    const m = new Map();
    holidays.forEach((h) => m.set(h.date, h));
    return m;
  }, [holidays]);

  // ✅ 공휴일 이벤트 변환
  const holidayEvents = useMemo(() => {
    return holidays.map((h) => ({
      id: `holiday-${h.date}`,
      title: h.name,
      start: h.date,
      allDay: true,
      backgroundColor: "transparent",
      borderColor: "transparent",
      extendedProps: {
        isHoliday: true,
        holiday_type: h.holiday_type,
      },
    }));
  }, [holidays]);



  const handleDateClick = (info) => {

    setCurrentDate(info.date);
    setView("timeGridDay");
    calRef.current?.getApi()?.gotoDate(info.date);
  };

  const handleEventClick = useCallback((info) => {
    setSelectedEvent(info.event.extendedProps.raw);
    setShowDetailModal(true);
  }, []);

  const handlePrev = () => {
    const calApi = calRef.current?.getApi();
    calApi?.prev();
    setCurrentDate(calApi?.getDate() || currentDate);
  };

  const handleNext = () => {
    const calApi = calRef.current?.getApi();
    calApi?.next();
    setCurrentDate(calApi?.getDate() || currentDate);
  };

  const handleToday = () => {
    const calApi = calRef.current?.getApi();
    calApi?.today();
    setCurrentDate(new Date());
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", minHeight: "100vh" }}>

      {/* ✅ 상단 컨트롤 영역 */}
      <div style={{ backgroundColor: "#fff", borderBottom: "1px solid #e0e0e0", padding: "10px 12px 10px" }}>

        {/* 1행: ◀ 날짜 ▶ */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
          <button
            onClick={handlePrev}
            style={{ border: "none", background: "transparent", fontSize: "20px", cursor: "pointer", color: "#444", padding: "4px 10px" }}
          >
            ◀
          </button>

          <span style={{ fontSize: "16px", fontWeight: "700", color: "#202124", whiteSpace: "nowrap" }}>
            {getHeaderText(view, currentDate)}
          </span>

          <button
            onClick={handleNext}
            style={{ border: "none", background: "transparent", fontSize: "20px", cursor: "pointer", color: "#444", padding: "4px 10px" }}
          >
            ▶
          </button>
        </div>

        {/* 2행: 일/주/월/연 탭 (풀너비) */}
        <div style={{ display: "flex", borderRadius: "8px", overflow: "hidden", border: "1px solid #dadce0", marginBottom: "8px" }}>
          {["timeGridDay", "timeGridWeek", "dayGridMonth", "multiMonthYear"].map((v, i) => (
            <button
              key={v}
              onClick={() => setView(v)}
              style={{
                flex: 1,
                padding: "8px 0",
                fontSize: "14px",
                fontWeight: view === v ? "700" : "400",
                color: view === v ? "#fff" : "#555",
                backgroundColor: view === v ? "#1a73e8" : "transparent",
                border: "none",
                borderLeft: i > 0 ? "1px solid #dadce0" : "none",
                cursor: "pointer",
              }}
            >
              {["일", "주", "월", "연"][i]}
            </button>
          ))}
        </div>

        {/* 3행: 오늘 + 새 일정 만들기 */}
        <div style={{ display: "flex", gap: "8px" }}>
          <button
            onClick={handleToday}
            style={{
              flex: 1,
              padding: "8px 0",
              border: "1px solid #dadce0",
              borderRadius: "8px",
              background: "transparent",
              fontSize: "13px",
              color: "#1a73e8",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            오늘
          </button>
          <button
            onClick={() => setShowCreateModal(true)}
            style={{
              flex: 2,
              padding: "8px 0",
              backgroundColor: "#1a73e8",
              color: "#fff",
              border: "none",
              borderRadius: "8px",
              fontSize: "13px",
              fontWeight: "600",
              cursor: "pointer",
            }}
          >
            ＋ 새 일정 만들기
          </button>
        </div>
      </div>

      {/* ✅ FullCalendar */}
      <div style={{ flex: 1, minHeight: "600px", overflow: "auto" }}>
        <FullCalendar
          ref={calRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin, multiMonthPlugin]}

          initialView="timeGridDay"
          headerToolbar={false}
          events={[...fcEvents, ...holidayEvents]}   // ✅ 공휴일 병합

          locale="ko"
          height="auto"
          contentHeight="auto"
          eventContent={renderEventContent}
          dateClick={handleDateClick}
          eventClick={handleEventClick}
          allDayText="공지사항"
          slotMinTime="06:00:00"
          slotMaxTime="24:00:00"
          slotDuration="00:30:00"
          slotLabelFormat={{ hour: "2-digit", minute: "2-digit", hour12: false }}
          expandRows={true}
          eventOrder={(a, b) => {
            const rankA = a.extendedProps.position_rank ?? 999;
            const rankB = b.extendedProps.position_rank ?? 999;
            return rankA - rankB;
          }}
          dayMaxEvents={view === "dayGridMonth" ? 3 : false}
          moreLinkContent={(args) => `+${args.num}개 더보기`}

          moreLinkClick={(info) => {
            setCurrentDate(info.date);
            setView("timeGridDay");
            calRef.current?.getApi()?.gotoDate(info.date);
          }}

          // ✅ 공휴일 표시
          dayCellContent={(arg) => {
            const dateStr = arg.date.toLocaleDateString("sv-SE");
            const holiday = holidayMap.get(dateStr);
            return (
              <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end" }}>
                <span>{arg.dayNumberText}</span>
                {holiday && (
                  <span style={{
                    fontSize: "9px",
                    color: "#d32f2f",
                    fontWeight: 600,
                    lineHeight: 1,
                    marginTop: 1,
                  }}>
                    {holiday.name}
                  </span>
                )}
              </div>
            );
          }}
          dayCellClassNames={(arg) => {
            const dateStr = arg.date.toLocaleDateString("sv-SE");
            return holidayMap.has(dateStr) ? ["is-holiday"] : [];
          }}

          datesSet={(info) => {
            setCurrentDate(info.view.currentStart);
          }}
        />
      </div>



      {/* ✅ 모달 */}
      <EventCreateModal
        isOpen={showCreateModal}
        onClose={() => {
          setShowCreateModal(false);
          fetchEvents();   // ✅ 모달 닫힐 때 한 번만 새로고침
        }}
        onSubmit={async (formData) => {
          // ✅ POST만 수행 (모달 닫기/새로고침은 onClose에서 일괄 처리)
          await api.post("/calendar/events/", formData);
        }}
      />



      <EventDetailModal
        isOpen={showDetailModal}
        event={selectedEvent}
        onClose={() => setShowDetailModal(false)}
        onUpdate={(updatedEvent) => {
          setServerEvents((prev) =>
            prev.map((ev) => (ev.id === updatedEvent.id ? updatedEvent : ev))
          );
          setRefreshTrigger((p) => p + 1);
          setShowDetailModal(false);
          alert("✅ 수정되었습니다.");
        }}
        onDelete={(deletedId) => {
          setServerEvents((prev) => prev.filter((ev) => ev.id !== deletedId));
          setShowDetailModal(false);
          alert("🗑️ 삭제되었습니다.");
        }}
      />
    </div>
  );
}